import React, { useState, useEffect } from 'react';
import { Package, Plus, Trash2, Edit2, AlertCircle, CheckCircle } from 'lucide-react';
import { useServicesStore } from '@/stores/servicesStore';
import { dependenciesApi } from '@/lib/dependencies';
import type { ServiceDependencyDto, CreateServiceDependencyDto } from '@/types/api';
import { Badge, Button, Checkbox, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, useToast } from '@nimbitlabs/ui';

interface ManageServiceDependenciesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceId: string;
  serviceName: string;
  dependencies: ServiceDependencyDto[];
  onRefresh: () => Promise<void>;
}

export const ManageServiceDependenciesModal: React.FC<ManageServiceDependenciesModalProps> = ({ 
  open, 
  onOpenChange, 
  serviceId,
  serviceName,
  dependencies,
  onRefresh
}) => {
  const { services } = useServicesStore();
  const { toast } = useToast();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<string>('');
  const [dependencyType, setDependencyType] = useState<string>('sync');
  const [isCritical, setIsCritical] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter out current service and already-dependent services (except when editing)
  const availableServices = services.filter(s => {
    if (s.id === serviceId) return false;
    // When editing, allow the currently selected service
    if (editingId && s.id === selectedServiceId) return true;
    // Otherwise, filter out services that are already dependencies
    return !dependencies.some(d => d.dependsOnServiceId === s.id);
  });

  const resetForm = () => {
    setSelectedServiceId('');
    setDependencyType('sync');
    setIsCritical(false);
    setIsAdding(false);
    setEditingId(null);
    setError(null);
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const handleAdd = async () => {
    if (!selectedServiceId) {
      setError('Please select a service');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const dto: CreateServiceDependencyDto = {
        serviceId,
        dependsOnServiceId: selectedServiceId,
        dependencyType,
        isCritical,
      };

      await dependenciesApi.createDependency(dto);
      // Reset form first to avoid UI flicker
      resetForm();
      // Then refresh data in background
      await onRefresh();
      // Show success message
      toast({
        title: 'Dependency added',
        description: 'The dependency has been successfully added.',
      });
    } catch (err: any) {
      console.error('Add dependency error:', err);
      if (err.message?.includes('409') || err.message?.includes('Conflict') || err.message?.includes('already exists')) {
        setError('This dependency already exists');
      } else {
        setError(err.message || 'Failed to add dependency');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (dep: ServiceDependencyDto) => {
    console.log('Editing dependency:', dep);
    setEditingId(dep.id);
    setSelectedServiceId(dep.dependsOnServiceId);
    setDependencyType(dep.dependencyType);
    setIsCritical(dep.isCritical);
    setIsAdding(true);
  };

  const handleUpdate = async () => {
    if (!editingId) return;

    setSaving(true);
    setError(null);

    try {
      await dependenciesApi.updateDependency(editingId, {
        dependencyType,
        isCritical,
      });
      // Reset form first to avoid UI flicker
      resetForm();
      // Then refresh data in background
      await onRefresh();
      // Show success message
      toast({
        title: 'Dependency updated',
        description: 'The dependency has been successfully updated.',
      });
    } catch (err: any) {
      setError(err.message || 'Failed to update dependency');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    console.log('Deleting dependency:', id);
    if (!confirm('Are you sure you want to remove this dependency?')) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      console.log('Calling deleteDependency API...');
      await dependenciesApi.deleteDependency(id);
      resetForm();
      await onRefresh();
      // Show success message
      toast({
        title: 'Dependency removed',
        description: 'The dependency has been successfully removed.',
      });
    } catch (err: any) {
      console.error('Delete failed:', err);
      setError(err.message || 'Failed to delete dependency');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Dependencies for {serviceName}</DialogTitle>
          <DialogDescription>
            Define which services this service depends on
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
              <AlertCircle className="h-4 w-4 text-destructive" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          )}

          {/* Current Dependencies */}
          {dependencies.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">Current Dependencies ({dependencies.length})</h4>
              <div className="space-y-2">
                {dependencies.map((dep) => (
                  <div key={dep.id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                    <div className="flex items-center gap-3 flex-1">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{dep.dependsOnServiceName}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {dep.dependencyType}
                          </Badge>
                          {dep.isCritical && (
                            <Badge variant="destructive" className="text-xs">
                              Critical
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(dep)}
                        disabled={saving}
                        className="h-8 w-8 p-0"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(dep.id)}
                        disabled={saving}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add/Edit Form */}
          {!isAdding ? (
            <div className="space-y-2">
              <Button 
                onClick={() => setIsAdding(true)} 
                variant="outline" 
                className="w-full"
                disabled={availableServices.length === 0}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Dependency
              </Button>
              {availableServices.length === 0 && (
                <p className="text-xs text-muted-foreground text-center">
                  {services.length === 0 ? 'Loading services...' : 'All available services already added as dependencies'}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
              <h4 className="text-sm font-semibold">
                {editingId ? 'Edit Dependency' : 'Add New Dependency'}
              </h4>
              
              <div className="space-y-3">
                {editingId ? (
                  <div className="space-y-2">
                    <Label>Depends On Service</Label>
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-md border">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        {dependencies.find(d => d.id === editingId)?.dependsOnServiceName || 'Unknown Service'}
                      </span>
                      <Badge variant="outline" className="ml-auto text-xs">
                        Cannot change
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      The target service cannot be changed. Delete and recreate to change the dependency target.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Depends On Service</Label>
                    <Select 
                      value={selectedServiceId} 
                      onValueChange={setSelectedServiceId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a service..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableServices.map(service => (
                          <SelectItem key={service.id} value={service.id}>
                            {service.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Dependency Type</Label>
                  <Select value={dependencyType} onValueChange={setDependencyType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sync">Synchronous (API Call)</SelectItem>
                      <SelectItem value="async">Asynchronous (Message Queue)</SelectItem>
                      <SelectItem value="data">Data Dependency</SelectItem>
                      <SelectItem value="api">API</SelectItem>
                      <SelectItem value="database">Database</SelectItem>
                      <SelectItem value="message-queue">Message Queue</SelectItem>
                      <SelectItem value="cache">Cache</SelectItem>
                      <SelectItem value="storage">Storage</SelectItem>
                      <SelectItem value="auth">Authentication</SelectItem>
                      <SelectItem value="external">External Service</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="critical" 
                    checked={isCritical}
                    onCheckedChange={(checked) => setIsCritical(checked === true)}
                  />
                  <label 
                    htmlFor="critical" 
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Critical Dependency
                  </label>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={resetForm}
                  disabled={saving}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={editingId ? handleUpdate : handleAdd}
                  disabled={!selectedServiceId || saving}
                  className="flex-1"
                >
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Add'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
